import { Box, Stack } from '@mui/material';
import {
  ActionDrawer,
  BackBubbleButton,
  CustomButton,
  getFactoringStatusChipType,
  getFactoringStatusLabel,
  HeroHeader,
  MobileTabs,
  StatusChip,
} from 'lib/mobileDesignSystem';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  FactoringOperationDetailProvider,
  getFactoringStatusColor,
  useFactoringOperationDetail,
} from './context';
import { getDrawerContent } from './drawers';
import { DrawerSection } from './drawerSections';
import {
  getOperationDetailTabComponent,
  OPERATION_DETAIL_DEFAULT_TAB,
  OPERATION_DETAIL_TAB_ITEMS,
} from './tabConfig';

const FactoringOperationDetailInner: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const requestId = id ?? '';
  const [activeTab, setActiveTab] = useState(OPERATION_DETAIL_DEFAULT_TAB);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSection, setDrawerSection] = useState<DrawerSection | null>(
    null,
  );
  const { status, quoteId, setRequestId } = useFactoringOperationDetail();
  React.useEffect(() => {
    setRequestId(id ?? null);
  }, [id, setRequestId]);

  const TabContent = getOperationDetailTabComponent(activeTab);

  const handleOpenDrawer = (section: DrawerSection) => {
    setDrawerSection(section);
    setDrawerOpen(true);
  };

  const isChatTab = activeTab === 'chat';

  return (
    <Stack
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: '100%',
        pb: isChatTab ? 0 : 14,
        px: 0,
      }}
      spacing={0}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <HeroHeader
          title={`Operación #${id}`}
          minHeight={60}
          fadeColor={status ? getFactoringStatusColor(status) : undefined}
          statusChip={
            status ? (
              <StatusChip
                text={getFactoringStatusLabel(status)}
                type={getFactoringStatusChipType(status)}
              />
            ) : undefined
          }
          sx={{ width: '100vw', mx: 'calc(-50vw + 50%)' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pt: 0.5,
            px: 2,
          }}
        >
          <BackBubbleButton onClick={() => navigate(-1)} />
        </Box>
      </Box>

      <Stack
        spacing={2}
        sx={{
          px: 2,
          pt: 2,
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box sx={{ width: '100%', flexShrink: 0 }}>
          <MobileTabs
            value={activeTab}
            items={OPERATION_DETAIL_TAB_ITEMS}
            onChange={setActiveTab}
          />
        </Box>
        {TabContent != null && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: isChatTab ? 'flex' : 'block',
              flexDirection: isChatTab ? 'column' : undefined,
              overflow: isChatTab ? 'hidden' : undefined,
            }}
          >
            <TabContent requestId={requestId} />
          </Box>
        )}
      </Stack>

      {!isChatTab && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
          }}
        >
          <Stack direction="row" spacing={2}>
            <CustomButton
              variant="primary"
              sx={{ flex: 1 }}
              disabled={!requestId}
              onClick={() => handleOpenDrawer(DrawerSection.Files)}
            >
              Archivos
            </CustomButton>
            <CustomButton
              variant="primary"
              sx={{ flex: 1 }}
              disabled={!quoteId}
              onClick={() => handleOpenDrawer(DrawerSection.Approvals)}
            >
              Aprobaciones
            </CustomButton>
          </Stack>
        </Box>
      )}

      <ActionDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {getDrawerContent(drawerSection)}
      </ActionDrawer>
    </Stack>
  );
};

const FactoringOperationDetail: React.FC = () => (
  <FactoringOperationDetailProvider>
    <FactoringOperationDetailInner />
  </FactoringOperationDetailProvider>
);

export default FactoringOperationDetail;
